import React from "react"
import { useLocation } from "react-router-dom"
import { Divider } from "antd"
import { QuestionCircleOutlined} from "@ant-design/icons"
import logoBlack from '../../../shared/assets/images/LogoBlack.svg'
import cqLogoBackground from '../../../shared/assets/images/cqLogoBackground.svg'
import completedInterview from '../../../shared/assets/images/completedInterview.svg'
import checkCircleBlue from '../../../shared/assets/images/checkCircleBlue.png'
import { useSelector } from "react-redux"
import { selectJobTitle } from "../slice/aiInterviewSlice"

const formatTime = (seconds: number): string => {
    const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

const InterviewCompleted: React.FC = () => {
    const location = useLocation();
    const state = (location.state || {});
    const elapsedTime = typeof state.elapsedTime === "number" ? state.elapsedTime : 0;
    const messagesCount = typeof state.messagesCount === "number" ? state.messagesCount : 0;
    const endedSuccessfully = typeof state.endedSuccessfully === "boolean" ? state.endedSuccessfully : true;

    const jobTitle = useSelector(selectJobTitle) || "Interview"

    return (
        <main className="relative flex flex-col min-w-screen min-h-screen overflow-hidden">
            <div className="absolute flex justify-end items-center -z-50 w-full h-full">
                <img src={cqLogoBackground} alt="" draggable={false} className="w-[35.5rem] h-[53.125rem] max-w-none" />
            </div>
            <div className="flex flex-wrap justify-between items-center w-full h-auto min-h-16 px-4 md:px-6 py-3">
                <div className="flex items-center gap-4 md:gap-6">
                    <img src={logoBlack} alt="PeopleCQ" className="h-[1.65rem]" />
                    <div className="text-sm font-medium text-gray-500 hidden sm:block">v2.x</div>
                </div>
                <div className="flex flex-wrap justify-end items-center gap-2 sm:gap-3 md:h-8 text-sm font-medium text-gray-500 mt-2 sm:mt-0">
                    <div>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    <Divider type="vertical" className="hidden sm:block h-[1.125rem] border-gray-500" />
                    <div>{jobTitle}</div>
                    <Divider type="vertical" className="hidden sm:block h-[1.125rem] border-gray-500" />
                    <QuestionCircleOutlined className="text-lg text-gray-500 cursor-pointer" />
                </div>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-10 lg:gap-[3.125rem] w-full h-full
                px-6 md:px-12 lg:px-[10rem] xl:pl-[18.75rem] xl:pr-[16.875rem] my-auto text-center lg:text-left">
                <div className="flex flex-col gap-6 w-full md:w-[28rem] h-auto lg:h-[21.375rem]">
                    <div className="flex flex-col gap-4">
                        <div className="text-2xl md:text-3xl leading-8 md:leading-10 font-bold text-neutral-900 dark:text-white">
                            {endedSuccessfully ? "Interview Completed!" : "Interview Terminated"}
                        </div>
                        <div className="text-base md:text-lg leading-6 text-gray-600">
                            {endedSuccessfully 
                                ? "Thank you for participating in this interview. We appreciate the time you took to speak with us today."
                                : "The interview was terminated because screen sharing was stopped. Please contact support if you need to reschedule."
                            }
                        </div>
                    </div>
                    <div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1 lg:w-[13.5rem]">
                                <div className="text-base text-gray-600">Duration</div>
                                <div className="text-xl md:text-2xl font-bold text-indigo-600">{formatTime(elapsedTime)}<span> min</span></div>
                            </div>
                            <div className="flex-1 lg:w-[13.5rem]">
                                <div className="text-base text-gray-600">Messages</div>
                                <div className="text-xl md:text-2xl font-bold text-indigo-600">{messagesCount}</div>
                            </div>
                        </div>
                        <Divider dashed className="border-gray-500 border-dashed mb-0" />
                    </div>
                    {endedSuccessfully && (
                        <div className="flex flex-col gap-[0.375rem] lg:h-6 text-base text-gray-600 items-center lg:items-start">
                            <div className="font-bold">Next Steps:</div>
                            <div className="flex gap-2 items-start md:items-center">
                                <img src={checkCircleBlue} alt="check" />
                                <div>Your interview has been recorded</div>
                            </div>
                            <div className="flex gap-1 items-start md:items-center">
                                <img src={checkCircleBlue} alt="check" />
                                <div>Our team will review your responses</div>
                            </div>
                            <div className="flex gap-1 items-start md:items-center">
                                <img src={checkCircleBlue} alt="check" />
                                <div>We'll contact you within 3-5 business days</div>
                            </div>
                        </div>
                    )}
                </div>
                <img src={completedInterview} alt="" className="w-[12rem] h-[12.5rem] md:w-[15rem] md:h-[15.5rem] object-contain" />
            </div>
        </main>
    )
}

export default InterviewCompleted